import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { deleteRequest, getRequest, postRequest, updateRequest } from "../services";
import toast from "react-hot-toast";

const initialState = {
  feeStructures: [],
  payments: [],
  loading: false,
  paymentLoading: false,
  error: null,
};

export const fetchFeeStructures = createAsyncThunk(
  "feeStructures/fetchFeeStructures",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest("/api/FeeManagement");
      if (response.status !== 200) {
        return rejectWithValue("Failed to fetch fee structures");
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUnpaidOrOverdueBills = createAsyncThunk(
  "feeStructures/fetchUnpaidOrOverdueBills",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest("/api/FeeManagement/GetUnpaidOrOverdueBills");
      if (response.status !== 200) {
        return rejectWithValue("Failed to fetch unpaid or overdue bills");
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllPayments = createAsyncThunk(
  "feeStructures/fetchAllPayments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest("/api/FeeManagement/GetAllPayments");
      if (response.status !== 200) {
        return rejectWithValue("Failed to fetch payments");
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const makePayment = createAsyncThunk(
  "feeStructures/makePayment",
  async (paymentData, { rejectWithValue }) => {
    try {
      // Create form data to handle file upload
      const formData = new FormData();
      
      // Append all payment data to form data
      if (paymentData.receiptAttachment) {
        formData.append('receiptAttachment', paymentData.receiptAttachment);
      }
      
      // Construct the URL with query parameters
      const url = `/api/FeeManagement/MakePayment?studentId=${paymentData.studentId}&amount=${paymentData.amount}`;
      
      const response = await postRequest(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.status !== 200) {
        return rejectWithValue("Failed to process payment");
      }
      
      toast.success("Payment processed successfully!");
      return response.data;
    } catch (error) {
      toast.error(error.message || "Failed to process payment");
      return rejectWithValue(error.message);
    }
  }
);

export const addFeeStructure = createAsyncThunk(
  "feeStructures/addFeeStructure",
  async (feeStructure, { rejectWithValue }) => {
    try {
      const response = await postRequest("/api/FeeManagement", feeStructure);
      toast.success("Fee Structure added successfully!");
      return response.data?.data;
    } catch (error) {
      toast.error("Error in adding Fee Structure!");
      return rejectWithValue(error.message);
    }
  }
);

export const updateFeeStructure = createAsyncThunk(
  "feeStructures/updateFeeStructure",
  async ({ id, feeStructure }, { rejectWithValue }) => {
    try {
      const response = await updateRequest(`/api/FeeManagement/${id}`, feeStructure);
      toast.success("Fee Structure updated successfully!");
      return response.data?.data;
    } catch (error) {
      toast.error("Error in updating Fee Structure!");
      return rejectWithValue(error.message);
    }
  }
);

export const deleteFeeStructure = createAsyncThunk(
  "feeStructures/deleteFeeStructure",
  async (id, { rejectWithValue }) => {
    try {
      await deleteRequest(`/api/FeeManagement/${id}`);
      toast.success("Fee Structure deleted successfully!");
      return id;
    } catch (error) {
      toast.error("Error in deleting Fee Structure!");
      return rejectWithValue(error.message);
    }
  }
);

const feeStructureSlice = createSlice({
  name: "feeStructures",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeeStructures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeeStructures.fulfilled, (state, action) => {
        state.loading = false;
        state.feeStructures = action.payload?.data?.items || [];
      })
      .addCase(fetchFeeStructures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch fee structures";
        toast.error(state.error);
      })
      .addCase(fetchUnpaidOrOverdueBills.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnpaidOrOverdueBills.fulfilled, (state, action) => {
        state.loading = false;
        state.unpaidOrOverdueBills = action.payload || [];
      })
      .addCase(fetchUnpaidOrOverdueBills.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch unpaid or overdue bills";
        toast.error(state.error);
      })
      .addCase(fetchAllPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload || [];
      })
      .addCase(fetchAllPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch payments";
        toast.error(state.error);
      })
      .addCase(makePayment.pending, (state) => {
        state.paymentLoading = true;
        state.error = null;
      })
      .addCase(makePayment.fulfilled, (state, action) => {
        state.paymentLoading = false;
        // You might want to update the unpaid bills list here or handle success in components
      })
      .addCase(makePayment.rejected, (state, action) => {
        state.paymentLoading = false;
        state.error = action.payload || "Failed to process payment";
      })
      .addCase(addFeeStructure.fulfilled, (state, action) => {
        state.feeStructures = [...state.feeStructures, action.payload];
      })
      .addCase(updateFeeStructure.fulfilled, (state, action) => {
        const index = state.feeStructures.findIndex((f) => f.id === action.payload.id);
        if (index !== -1) state.feeStructures[index] = action.payload;
      })
      .addCase(deleteFeeStructure.fulfilled, (state, action) => {
        state.feeStructures = state.feeStructures.filter((f) => f.id !== action.payload);
      });
  },
});

export default feeStructureSlice.reducer;